import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Box, Button, type DialogProps } from '@mui/material';
import { TreeView } from '@mui/x-tree-view';
import { useCallback, useState, type SyntheticEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAddToRole } from '~/api/roles';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { useFolderContext } from '~/contexts';
import type { FolderTree } from '~/graphql/scalars';
import { CheckboxTreeItem } from '../../RoleContentFolderList/components';
import { getDescendants } from '../../lib';
import type { RolePlaylistFolderList__Role as Role } from '../RolePlaylistFolderList.generated';

export interface AddPlaylistFoldersDialogProps extends Omit<DialogProps, 'role'> {
  role: Role;
}

export const AddPlaylistFoldersDialog = ({ role, ...props }: AddPlaylistFoldersDialogProps) => {
  const { t } = useTranslation();
  const { folderTree } = useFolderContext();
  const [expanded, setExpanded] = useState<string[]>([String(folderTree.id)]);
  const [selected, setSelected] = useState<string[]>(
    role.playlistFolderRules.map((x) => String(x.playlistFolder.id)),
  );

  const [add, { called }] = useAddToRole({
    variables: {
      roleId: role.id,
      playlistFolderRules: selected.map((id) => ({ playlistFolderId: parseInt(id) })),
    },
  });

  const close = () => props.onClose?.({}, 'backdropClick');

  const handleSelect = useCallback(
    (event: SyntheticEvent<Element, Event>, nodeIds: string[]) => {
      if ((event.target as Node).nodeName !== 'DIV') return;
      setSelected((current) => {
        const id = nodeIds.at(0);
        if (!id) return current;
        if (current.includes(id)) return current.filter((x) => x !== id);

        const descendantIds = getDescendants(folderTree, parseInt(id)).map((x) => x.id);
        return [...current.filter((x) => !descendantIds.includes(parseInt(x))), id];
      });
    },
    [folderTree],
  );

  const handleToggle = useCallback((event: SyntheticEvent<Element, Event>, nodeIds: string[]) => {
    if ((event.target as Node).nodeName === 'DIV') return;
    setExpanded(() => nodeIds);
  }, []);

  const renderTree = useCallback(
    (children: FolderTree[], disabled: boolean) =>
      children.map((x) => (
        <CheckboxTreeItem disabled={disabled} key={x.id} nodeId={String(x.id)} label={x.name}>
          {renderTree(x.children, disabled || selected.includes(String(x.id)))}
        </CheckboxTreeItem>
      )),
    [selected],
  );

  return (
    <Dialog {...props}>
      <DialogTitle onClose={close}>
        {t('pages.roles.dialogs.add_playlist_folders.title')}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DialogContentTitle>
          <Trans
            i18nKey="pages.roles.dialogs.add_playlist_folders.description"
            values={{ role_name: role.name }}
            components={{ b: <b /> }}
          />
        </DialogContentTitle>

        <Box>
          <TreeView
            defaultCollapseIcon={<ExpandMore />}
            defaultExpandIcon={<ChevronRight />}
            expanded={expanded}
            multiSelect
            onNodeSelect={handleSelect}
            onNodeToggle={handleToggle}
            selected={selected}
          >
            <CheckboxTreeItem
              nodeId={String(folderTree.id)}
              label={t('pages.roles.dialogs.add_playlist_folders.all_folders')}
            >
              {renderTree(folderTree.children, selected.includes(String(folderTree.id)))}
            </CheckboxTreeItem>
          </TreeView>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button disabled={called} onClick={close} variant="outlined">
          {t('common.actions.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={called || !selected.length}
          onClick={() => {
            close();
            return add();
          }}
          variant="contained"
        >
          {t('common.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
